import React, { useState, useEffect } from 'react';
import { trackPageView, trackButtonClick } from '../analytics';
import SEOMetaTags from '../components/SEOMetaTags';
import Header from '../components/Header';

const CompressPdf = () => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [streamMode, setStreamMode] = useState('compress');
  const [objectStreams, setObjectStreams] = useState('preserve');
  const [normalizeContent, setNormalizeContent] = useState(false);
  const [recompressFlate, setRecompressFlate] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    trackPageView('/compress-pdf', 'Compress PDF Page');
  }, []);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file && file.type === 'application/pdf') {
      if (file.size > 10 * 1024 * 1024) { // 10MB limit
        setError('File size must be less than 10MB');
        setSelectedFile(null);
        return;
      }
      setSelectedFile(file);
      setError('');
      setSuccess(false);
    } else {
      setError('Please select a valid PDF file');
      setSelectedFile(null);
    }
  };

  const handleCompress = async () => {
    if (!selectedFile) {
      setError('Please select a PDF file first');
      return;
    }

    setIsLoading(true);
    setError('');
    setSuccess(false);

    try {
      trackButtonClick('compress_pdf', 'compress_pdf');

      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('stream_mode', streamMode);
      formData.append('object_streams', objectStreams);
      formData.append('normalize_content', normalizeContent);
      formData.append('recompress_flate', recompressFlate);

      const response = await fetch('https://api.temp2doc.com/organise/compress', {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        throw new Error(`Error: ${response.status} ${response.statusText}`);
      }

      // Get the compressed PDF
      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);

      // Create a download link and trigger it
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = `compressed_${selectedFile.name}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      setSuccess(true);
      trackButtonClick('compress_success', 'compress_pdf');
    } catch (err) {
      trackButtonClick('compress_error', 'compress_pdf');
      setError(`Failed to compress PDF: ${err.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <SEOMetaTags 
          title="Compress PDF - Reduce PDF File Size Online Free | Temp2Doc"
          description="Compress and optimize PDF files online for free with advanced compression options. Reduce PDF size by up to 90% for web delivery, email attachments, mobile optimization, and archival storage. Stream control, object stream optimization, and content normalization available."
          keywords="compress PDF, reduce PDF size, optimize PDF, PDF compression, shrink PDF, PDF optimizer, reduce file size, compress PDF online free, PDF size reducer, optimize PDF for web, email PDF compression, mobile PDF optimization, PDF compressor, small PDF, minimize PDF size, PDF file compression tool"
          canonicalUrl="https://temp2doc.com/compress-pdf"
          structuredData={{
            "@context": "https://schema.org",
            "@type": "SoftwareApplication",
            "name": "Compress PDF",
            "applicationCategory": "BusinessApplication",
            "operatingSystem": "Web browser",
            "offers": {
              "@type": "Offer",
              "price": "0",
              "priceCurrency": "USD"
            },
            "description": "Compress and optimize PDF files online for free. Reduce file size with advanced compression options for web delivery, email, and mobile optimization.",
            "aggregateRating": {
              "@type": "AggregateRating",
              "ratingValue": "4.9",
              "ratingCount": "1580"
            },
            "featureList": [
              "Reduce PDF file size up to 90%",
              "Advanced stream compression control",
              "Object stream optimization",
              "Content normalization",
              "Flate stream recompression for old PDFs",
              "Perfect for web delivery and email",
              "No file size limits up to 10MB",
              "Secure processing with automatic file deletion",
              "No registration required",
              "Free online tool"
            ]
          }}
        />
      <Header />
      <main className="min-h-screen bg-gradient-to-b from-white to-blue-50 pt-24 pb-12 px-4 sm:px-6 lg:px-8">
        {/* Hero Section */}
        <section className="max-w-6xl mx-auto text-center mb-12">
          <div className="inline-block p-3 bg-primary-100 rounded-full mb-4">
            <svg className="w-12 h-12 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
            </svg>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Compress PDF - Optimize File Size
          </h1>
          <p className="text-xl text-gray-600 mb-8">
            Reduce PDF file size with advanced compression options. Perfect for web delivery, email attachments, mobile optimization, and archival storage.
          </p>
        </section>

        {/* Main Tool Section */}
        <section className="max-w-6xl mx-auto mb-12">
          <div className="bg-white rounded-xl shadow-lg p-8">
            {/* File Upload */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select PDF File (Max 10MB)
              </label>
              <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-lg hover:border-primary-500 transition-colors">
                <div className="space-y-1 text-center">
                  <svg className="mx-auto h-12 w-12 text-gray-400" stroke="currentColor" fill="none" viewBox="0 0 48 48">
                    <path d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  <div className="flex text-sm text-gray-600">
                    <label htmlFor="pdf-upload" className="relative cursor-pointer bg-white rounded-md font-medium text-primary-600 hover:text-primary-500 focus-within:outline-none">
                      <span>Upload a file</span>
                      <input id="pdf-upload" name="pdf-upload" type="file" className="sr-only" accept=".pdf" onChange={handleFileChange} disabled={isLoading} />
                    </label>
                    <p className="pl-1">or drag and drop</p>
                  </div>
                  <p className="text-xs text-gray-500">PDF up to 10MB</p>
                </div>
              </div>
              {selectedFile && (
                <p className="mt-2 text-sm text-gray-600">
                  Selected: {selectedFile.name} ({(selectedFile.size / 1024 / 1024).toFixed(2)} MB)
                </p>
              )}
            </div>

            {/* Compression Options */}
            <div className="space-y-6 mb-6">
              {/* Stream Mode */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Stream Compression Mode
                </label>
                <div className="space-y-2">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="streamMode"
                      value="compress"
                      checked={streamMode === 'compress'}
                      onChange={(e) => setStreamMode(e.target.value)}
                      className="mr-2"
                      disabled={isLoading}
                    />
                    <span className="text-sm">
                      <strong>Compress:</strong> Maximize compression (recommended for most cases)
                    </span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="streamMode"
                      value="preserve"
                      checked={streamMode === 'preserve'}
                      onChange={(e) => setStreamMode(e.target.value)}
                      className="mr-2"
                      disabled={isLoading}
                    />
                    <span className="text-sm">
                      <strong>Preserve:</strong> Keep original compression
                    </span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="streamMode"
                      value="uncompress"
                      checked={streamMode === 'uncompress'}
                      onChange={(e) => setStreamMode(e.target.value)}
                      className="mr-2"
                      disabled={isLoading}
                    />
                    <span className="text-sm">
                      <strong>Uncompress:</strong> Expand streams (larger file, better for debugging)
                    </span>
                  </label>
                </div>
              </div>

              {/* Object Streams */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Object Streams
                </label>
                <div className="space-y-2">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="objectStreams"
                      value="generate"
                      checked={objectStreams === 'generate'}
                      onChange={(e) => setObjectStreams(e.target.value)}
                      className="mr-2"
                      disabled={isLoading}
                    />
                    <span className="text-sm">
                      <strong>Generate:</strong> More compact, modern PDF format
                    </span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="objectStreams"
                      value="preserve"
                      checked={objectStreams === 'preserve'}
                      onChange={(e) => setObjectStreams(e.target.value)}
                      className="mr-2"
                      disabled={isLoading}
                    />
                    <span className="text-sm">
                      <strong>Preserve:</strong> Keep original object streams (recommended)
                    </span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="objectStreams"
                      value="disable"
                      checked={objectStreams === 'disable'}
                      onChange={(e) => setObjectStreams(e.target.value)}
                      className="mr-2"
                      disabled={isLoading}
                    />
                    <span className="text-sm">
                      <strong>Disable:</strong> Best compatibility with old readers
                    </span>
                  </label>
                </div>
              </div>

              {/* Additional Options */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Advanced Options
                </label>
                <div className="space-y-2">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={normalizeContent}
                      onChange={(e) => setNormalizeContent(e.target.checked)}
                      className="mr-2"
                      disabled={isLoading}
                    />
                    <span className="text-sm">
                      <strong>Normalize Content:</strong> Standardize content streams for better compression
                    </span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={recompressFlate}
                      onChange={(e) => setRecompressFlate(e.target.checked)}
                      className="mr-2"
                      disabled={isLoading}
                    />
                    <span className="text-sm">
                      <strong>Recompress Flate:</strong> Recompress flate streams (good for old PDFs)
                    </span>
                  </label>
                </div>
              </div>
            </div>

            {/* Error/Success Messages */}
            {error && (
              <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-600">{error}</p>
              </div>
            )}

            {success && (
              <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                <p className="text-sm text-green-600">PDF compressed successfully! Your download should start automatically.</p>
              </div>
            )}

            <button
              onClick={handleCompress}
              disabled={!selectedFile || isLoading}
              className="w-full bg-primary-600 text-white py-3 px-6 rounded-lg font-semibold hover:bg-primary-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
            >
              {isLoading ? 'Compressing PDF...' : 'Compress PDF'}
            </button>

            {/* Info Box */}
            <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <h3 className="text-sm font-semibold text-blue-800 mb-2">💡 Compression Tips</h3>
              <ul className="text-sm text-blue-700 space-y-1">
                <li>• Use "Compress" mode for maximum file size reduction</li>
                <li>• Enable "Generate" object streams for modern PDFs</li>
                <li>• Try "Recompress Flate" for old or poorly compressed PDFs</li>
                <li>• Typical compression: 30-90% file size reduction</li>
              </ul>
            </div>
          </div>
        </section>

        {/* How It Works */}
        <section className="max-w-6xl mx-auto mb-12">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-8">How It Works</h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white rounded-lg p-6 shadow-sm">
              <div className="flex items-center justify-center w-12 h-12 bg-primary-100 text-primary-600 rounded-full mb-4 mx-auto font-bold text-lg">
                1
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2 text-center">Upload Your PDF</h3>
              <p className="text-gray-600 text-center">
                Select the PDF file you want to compress. Files up to 10MB are supported.
              </p>
            </div>
            <div className="bg-white rounded-lg p-6 shadow-sm">
              <div className="flex items-center justify-center w-12 h-12 bg-primary-100 text-primary-600 rounded-full mb-4 mx-auto font-bold text-lg">
                2
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2 text-center">Choose Compression Options</h3>
              <p className="text-gray-600 text-center">
                Select compression settings or use defaults for optimal results. Advanced options available for fine-tuning.
              </p>
            </div>
            <div className="bg-white rounded-lg p-6 shadow-sm">
              <div className="flex items-center justify-center w-12 h-12 bg-primary-100 text-primary-600 rounded-full mb-4 mx-auto font-bold text-lg">
                3
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2 text-center">Download Compressed PDF</h3>
              <p className="text-gray-600 text-center">
                Get your optimized PDF with significantly reduced file size, perfect for web and email.
              </p>
            </div>
          </div>
        </section>

        {/* Key Features */}
        <section className="max-w-6xl mx-auto mb-12">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-8">Key Features</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="bg-white rounded-lg p-6 shadow-sm">
              <div className="text-primary-600 mb-3">
                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Fast Compression</h3>
              <p className="text-gray-600 text-sm">
                Advanced algorithms reduce file size by 30-90% in seconds without quality loss.
              </p>
            </div>
            <div className="bg-white rounded-lg p-6 shadow-sm">
              <div className="text-primary-600 mb-3">
                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Advanced Options</h3>
              <p className="text-gray-600 text-sm">
                Fine-tune compression with stream modes, object streams, and content normalization.
              </p>
            </div>
            <div className="bg-white rounded-lg p-6 shadow-sm">
              <div className="text-primary-600 mb-3">
                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Secure & Private</h3>
              <p className="text-gray-600 text-sm">
                Your files are processed securely and automatically deleted after compression.
              </p>
            </div>
            <div className="bg-white rounded-lg p-6 shadow-sm">
              <div className="text-primary-600 mb-3">
                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 15a4 4 0 004 4h9a5 5 0 10-.1-9.999 5.002 5.002 0 10-9.78 2.096A4.001 4.001 0 003 15z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Web Optimized</h3>
              <p className="text-gray-600 text-sm">
                Perfect for websites, email attachments, and mobile devices with smaller file sizes.
              </p>
            </div>
            <div className="bg-white rounded-lg p-6 shadow-sm">
              <div className="text-primary-600 mb-3">
                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">No Time Limits</h3>
              <p className="text-gray-600 text-sm">
                Compress as many PDFs as you need with no restrictions or waiting times.
              </p>
            </div>
            <div className="bg-white rounded-lg p-6 shadow-sm">
              <div className="text-primary-600 mb-3">
                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Quality Preserved</h3>
              <p className="text-gray-600 text-sm">
                Smart compression maintains document quality while reducing file size significantly.
              </p>
            </div>
          </div>
        </section>

        {/* Related Tools Section */}
        <section className="max-w-6xl mx-auto">
          <nav className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
            <h2 className="text-2xl font-bold text-gray-900 mb-4 text-center">Related PDF Tools</h2>
          <ul className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            <li>
              <a href="/chat-with-pdf" className="block bg-white border border-gray-200 rounded-lg p-4 text-center shadow-sm hover:shadow transition-shadow">
                <span className="text-primary-600 font-medium">Chat PDF With AI</span>
              </a>
            </li>
            <li>
              <a href="/merge-pdf" className="block bg-white border border-gray-200 rounded-lg p-4 text-center shadow-sm hover:shadow transition-shadow">
                <span className="text-primary-600 font-medium">Merge PDF</span>
              </a>
            </li>
            <li>
              <a href="/split-remove-rotate-pdf" className="block bg-white border border-gray-200 rounded-lg p-4 text-center shadow-sm hover:shadow transition-shadow">
                <span className="text-primary-600 font-medium">Split Remove Rotate PDF</span>
              </a>
            </li>
            <li>
              <a href="/pdf-to-word" className="block bg-white border border-gray-200 rounded-lg p-4 text-center shadow-sm hover:shadow transition-shadow">
                <span className="text-primary-600 font-medium">PDF to Word</span>
              </a>
            </li>
            <li>
              <a href="/pdf-to-excel" className="block bg-white border border-gray-200 rounded-lg p-4 text-center shadow-sm hover:shadow transition-shadow">
                <span className="text-primary-600 font-medium">PDF to Excel</span>
              </a>
            </li>
            <li>
              <a href="/pdf-to-image" className="block bg-white border border-gray-200 rounded-lg p-4 text-center shadow-sm hover:shadow transition-shadow">
                <span className="text-primary-600 font-medium">PDF to Image</span>
              </a>
            </li>
            <li>
              <a href="/pdf-to-powerpoint" className="block bg-white border border-gray-200 rounded-lg p-4 text-center shadow-sm hover:shadow transition-shadow">
                <span className="text-primary-600 font-medium">PDF to PowerPoint</span>
              </a>
            </li>
            <li>
              <a href="/protect-pdf" className="block bg-white border border-gray-200 rounded-lg p-4 text-center shadow-sm hover:shadow transition-shadow">
                <span className="text-primary-600 font-medium">Protect PDF</span>
              </a>
            </li>
            <li>
              <a href="/unlock-pdf" className="block bg-white border border-gray-200 rounded-lg p-4 text-center shadow-sm hover:shadow transition-shadow">
                <span className="text-primary-600 font-medium">Unlock PDF</span>
              </a>
            </li>
            <li>
              <a href="/transform-pdf" className="block bg-white border border-gray-200 rounded-lg p-4 text-center shadow-sm hover:shadow transition-shadow">
                <span className="text-primary-600 font-medium">Repair & Transform PDF</span>
              </a>
            </li>
            <li>
              <a href="/diagnose-pdf" className="block bg-white border border-gray-200 rounded-lg p-4 text-center shadow-sm hover:shadow transition-shadow">
                <span className="text-primary-600 font-medium">Verify & Diagnose PDF</span>
              </a>
            </li>
            <li>
              <a href="/" className="block bg-white border border-gray-200 rounded-lg p-4 text-center shadow-sm hover:shadow transition-shadow">
                <span className="text-primary-600 font-medium">Back to Home</span>
              </a>
            </li>
          </ul>
          </nav>
        </section>
      </main>
    </>
  );
};

export default CompressPdf;
